/** Panneau latéral fluide « Majordome » — analyse IA du calque sélectionné. */
import { useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, ShieldCheck, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MajordomePanel = ({ open, onClose, layer, layerLabel, color, aggregates, getSnapshot }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  const analyze = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.post(`${API}/majordome/analyze`, {
        layer, layer_label: layerLabel, aggregates,
        canvas_b64: getSnapshot?.() || null,
      }, { withCredentials: true });
      setAnalysis(r.data.analysis);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Le Majordome est indisponible');
    } finally { setLoading(false); }
  };

  return (
    <aside className="absolute top-0 right-0 bottom-0 z-[710] w-[350px] max-w-[92vw] flex flex-col transition-transform duration-300 ease-out"
           style={{
             transform: open ? 'translateX(0)' : 'translateX(105%)',
             background: 'rgba(14,15,20,0.72)', backdropFilter: 'blur(22px)',
             borderLeft: '1px solid rgba(217,160,91,0.3)',
           }}
           data-testid="majordome-panel">
      <div className="px-4 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(217,160,91,0.18)', border: '1px solid rgba(217,160,91,0.45)' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#D9A05B' }} />
          </span>
          <div>
            <h3 className="text-[13px] font-bold text-white">Majordome</h3>
            <p className="text-[9.5px] text-gray-500">Conseiller patrimonial privé</p>
          </div>
        </div>
        <button type="button" onClick={onClose} data-testid="majordome-close">
          <X className="w-4 h-4 text-gray-400 hover:text-white" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
        <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}40` }}>
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Calque observé</p>
          <p className="text-[13px] font-bold mt-0.5" style={{ color }}>{layerLabel}</p>
          <p className="text-[10.5px] text-gray-400 mt-1">
            {aggregates.count} position(s) · {aggregates.total_bucket || '—'}
          </p>
        </div>

        <button type="button" onClick={analyze} disabled={loading}
                className="w-full h-10 rounded-xl text-[12.5px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #D9A05B, #B9803F)', color: '#0A0A0D' }}
                data-testid="majordome-analyze-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Analyser ce calque
        </button>

        {error && <p className="text-[11px] text-rose-300" data-testid="majordome-error">{error}</p>}
        {analysis && (
          <div className="rounded-xl p-3.5 text-[12px] leading-relaxed text-gray-200 whitespace-pre-wrap"
               style={{ background: 'rgba(217,160,91,0.07)', border: '1px solid rgba(217,160,91,0.25)' }}
               data-testid="majordome-analysis">
            {analysis}
          </div>
        )}
      </div>

      <div className="px-4 py-3 flex items-start gap-2 text-[9.5px] text-gray-500 leading-snug"
           style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
        Flux anonymisé : seuls des agrégats (ordres de grandeur, régions) et le
        dessin abstrait du calque sont transmis. Jamais d'identité, jamais de
        valeurs exactes, aucune conservation côté serveur.
      </div>
    </aside>
  );
};
